import React, { useState, useEffect } from 'react';
import { createExam, getAllExams } from '../../services/admin';
import { getSubjects } from '../../services/content';

const ExamManagement = () => {
  const [exams, setExams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [examData, setExamData] = useState({
    title: '',
    description: '',
    subjectId: '',
    levelId: '',
    duration: 60,
    totalMarks: 100,
    passingMarks: 50,
    questions: []
  });
  const [currentQuestion, setCurrentQuestion] = useState({
    question: '',
    type: 'multiple_choice',
    options: ['', '', '', ''],
    correctAnswer: 0,
    marks: 1
  });

  // Hardcoded levels as requested
  const levels = [
    { id: 'grade7', name: 'Grade 7' },
    { id: 'grade12', name: 'Grade 12' }
  ];

  const getSubjectName = (subjectId) => {
    const subject = subjects.find(s => s.id === subjectId);
    return subject ? subject.name : subjectId;
  };

  const getLevelName = (levelId) => {
    const level = levels.find(l => l.id === levelId);
    return level ? level.name : levelId;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch exams and subjects
        const [examsData, subjectsData] = await Promise.all([
          getAllExams(),
          getSubjects('grade7') // Fetch subjects for grade7 as default
        ]);
        
        setExams(examsData);
        setSubjects(subjectsData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleAddQuestion = () => {
    if (!currentQuestion.question.trim()) {
      alert('Please enter a question');
      return;
    }

    const question = {
      ...currentQuestion,
      id: Date.now()
    };

    setExamData({
      ...examData,
      questions: [...examData.questions, question]
    });

    // Reset current question
    setCurrentQuestion({
      question: '',
      type: 'multiple_choice',
      options: ['', '', '', ''],
      correctAnswer: 0,
      marks: 1
    });
  };

  const handleRemoveQuestion = (index) => {
    setExamData({
      ...examData,
      questions: examData.questions.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (examData.questions.length === 0) {
      alert('Please add at least one question');
      return;
    }

    try {
      await createExam(examData);
      
      // Reset form
      setExamData({
        title: '',
        description: '',
        subjectId: '',
        levelId: '',
        duration: 60,
        totalMarks: 100,
        passingMarks: 50,
        questions: []
      });
      setCurrentStep(1);
      setShowCreateModal(false);
      
      // Refresh exams list
      const examsData = await getAllExams();
      setExams(examsData);
      
      alert('Exam created successfully!');
    } catch (error) {
      console.error('Error creating exam:', error);
      alert('Failed to create exam. Please try again.');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="exam-management">
        <div className="loading-container">
          <div className="loading-spinner">Loading exams...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="exam-management">
      <div className="page-header">
        <h1>Exam Management</h1>
        <p>Create and manage practice examinations</p>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn btn-primary"
        >
          + Create Exam
        </button>
      </div>

      {/* Exams Grid */}
      <div className="exams-grid">
        {exams.map((exam) => (
          <div key={exam.id} className="exam-card">
            <div className="exam-header">
              <h3>{exam.title}</h3>
              <span className="exam-status">
                {exam.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            
            <div className="exam-info">
              <p><strong>Subject:</strong> {getSubjectName(exam.subjectId)}</p>
              <p><strong>Level:</strong> {getLevelName(exam.levelId)}</p>
              <p><strong>Duration:</strong> {exam.duration} minutes</p>
              <p><strong>Questions:</strong> {exam.questions?.length || 0}</p>
              <p><strong>Total Marks:</strong> {exam.totalMarks}</p>
              <p><strong>Passing:</strong> {exam.passingMarks} ({Math.round((exam.passingMarks / exam.totalMarks) * 100)}%)</p>
            </div>

            <div className="exam-actions">
              <button className="btn btn-sm btn-primary">
                Edit
              </button>
              <button className="btn btn-sm btn-danger">
                Delete
              </button>
            </div>

            <div className="exam-footer">
              <small>Created: {formatDate(exam.createdAt)}</small>
            </div>
          </div>
        ))}
      </div>

      {exams.length === 0 && (
        <div className="no-content">
          <h3>No exams created yet</h3>
          <p>Start by creating your first practice exam.</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary"
          >
            Create First Exam
          </button>
        </div>
      )}

      {/* Create Exam Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <div className="modal-header">
              <h3>Create New Exam</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="modal-close"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-content">
              {/* Step 1: Basic Info */}
              {currentStep === 1 && (
                <div className="step-content">
                  <h4>Step 1: Basic Information</h4>
                  
                  <div className="form-group">
                    <label>Exam Title *</label>
                    <input
                      type="text"
                      value={examData.title}
                      onChange={(e) => setExamData({...examData, title: e.target.value})}
                      className="form-control"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={examData.description}
                      onChange={(e) => setExamData({...examData, description: e.target.value})}
                      className="form-control"
                      rows={3}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Subject *</label>
                      <select
                        value={examData.subjectId}
                        onChange={(e) => setExamData({...examData, subjectId: e.target.value})}
                        className="form-control"
                        required
                      >
                        <option value="">Select Subject</option>
                        {subjects.map((subject) => (
                          <option key={subject.id} value={subject.id}>
                            {subject.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Level *</label>
                      <select
                        value={examData.levelId}
                        onChange={(e) => setExamData({...examData, levelId: e.target.value})}
                        className="form-control"
                        required
                      >
                        <option value="">Select Level</option>
                        {levels.map((level) => (
                          <option key={level.id} value={level.id}>
                            {level.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Duration (minutes) *</label>
                      <input
                        type="number"
                        value={examData.duration}
                        onChange={(e) => setExamData({...examData, duration: parseInt(e.target.value)})}
                        className="form-control"
                        min="10"
                        max="180"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Total Marks *</label>
                      <input
                        type="number"
                        value={examData.totalMarks}
                        onChange={(e) => setExamData({...examData, totalMarks: parseInt(e.target.value)})}
                        className="form-control"
                        min="1"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Passing Marks *</label>
                    <input
                      type="number"
                      value={examData.passingMarks}
                      onChange={(e) => setExamData({...examData, passingMarks: parseInt(e.target.value)})}
                      className="form-control"
                      min="1"
                      max={examData.totalMarks}
                      required
                    />
                  </div>
                </div>
              )}

              {/* Step 2: Questions */}
              {currentStep === 2 && (
                <div className="step-content">
                  <h4>Step 2: Add Questions</h4>
                  
                  <div className="questions-builder">
                    {examData.questions.map((question, index) => (
                      <div key={question.id} className="question-item">
                        <div className="question-header">
                          <span>Question {index + 1}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveQuestion(index)}
                            className="btn btn-sm btn-danger"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="question-content">
                          <p>{question.question}</p>
                          {question.type === 'multiple_choice' && (
                            <ol>
                              {question.options.map((option, i) => (
                                <li key={i}>{option}</li>
                              ))}
                            </ol>
                          )}
                          <small>Marks: {question.marks}</small>
                        </div>
                      </div>
                    ))}

                    <div className="add-question-form">
                      <h5>Add New Question</h5>
                      
                      <div className="form-group">
                        <label>Question *</label>
                        <textarea
                          value={currentQuestion.question}
                          onChange={(e) => setCurrentQuestion({...currentQuestion, question: e.target.value})}
                          className="form-control"
                          rows={3}
                          required
                        />
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>Type</label>
                          <select
                            value={currentQuestion.type}
                            onChange={(e) => setCurrentQuestion({...currentQuestion, type: e.target.value})}
                            className="form-control"
                          >
                            <option value="multiple_choice">Multiple Choice</option>
                            <option value="true_false">True/False</option>
                            <option value="short_answer">Short Answer</option>
                          </select>
                        </div>

                        <div className="form-group">
                          <label>Marks</label>
                          <input
                            type="number"
                            value={currentQuestion.marks}
                            onChange={(e) => setCurrentQuestion({...currentQuestion, marks: parseInt(e.target.value)})}
                            className="form-control"
                            min="1"
                            required
                          />
                        </div>
                      </div>

                      {currentQuestion.type === 'multiple_choice' && (
                        <div className="options-group">
                          <label>Options</label>
                          {currentQuestion.options.map((option, index) => (
                            <div key={index} className="option-input">
                              <input
                                type="text"
                                value={option}
                                onChange={(e) => {
                                  const newOptions = [...currentQuestion.options];
                                  newOptions[index] = e.target.value;
                                  setCurrentQuestion({...currentQuestion, options: newOptions});
                                }}
                                className="form-control"
                                placeholder={`Option ${index + 1}`}
                                required
                              />
                            </div>
                          ))}
                        </div>
                      )}

                      {currentQuestion.type === 'multiple_choice' && (
                        <div className="form-group">
                          <label>Correct Answer</label>
                          <select
                            value={currentQuestion.correctAnswer}
                            onChange={(e) => setCurrentQuestion({...currentQuestion, correctAnswer: parseInt(e.target.value)})}
                            className="form-control"
                            required
                          >
                            <option value={0}>Option 1</option>
                            <option value={1}>Option 2</option>
                            <option value={2}>Option 3</option>
                            <option value={3}>Option 4</option>
                          </select>
                        </div>
                      )}

                      {currentQuestion.type === 'true_false' && (
                        <div className="form-group">
                          <label>Correct Answer</label>
                          <select
                            value={currentQuestion.correctAnswer}
                            onChange={(e) => setCurrentQuestion({...currentQuestion, correctAnswer: e.target.value === 'true'})}
                            className="form-control"
                            required
                          >
                            <option value="true">True</option>
                            <option value="false">False</option>
                          </select>
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={handleAddQuestion}
                        className="btn btn-secondary"
                      >
                        Add Question
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="modal-actions">
                {currentStep === 1 && (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="btn btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => setCurrentStep(2)}
                      className="btn btn-primary"
                    >
                      Next: Add Questions
                    </button>
                  </>
                )}
                
                {currentStep === 2 && (
                  <>
                    <button
                      type="button"
                      onClick={() => setCurrentStep(1)}
                      className="btn btn-secondary"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={examData.questions.length === 0}
                    >
                      Create Exam ({examData.questions.length} questions)
                    </button>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamManagement;
